import { USER_TYPE } from '@/common/constants';
import { FASTSHIFT_PATH_KEYS } from '@/common/routes/constants';
import type { UserAccess } from '@/state/types';

type UserType = (typeof USER_TYPE)[keyof typeof USER_TYPE];

export type FastShiftRouteAccessPolicy = {
	allowedUserTypes: UserType[];
	requireMoveTicketAccess: boolean;
};

export const FASTSHIFT_ROUTE_ACCESS_POLICIES: Record<
	FASTSHIFT_PATH_KEYS,
	FastShiftRouteAccessPolicy
> = {
	[FASTSHIFT_PATH_KEYS.FASTSHIFT]: {
		allowedUserTypes: [USER_TYPE.INTERNAL, USER_TYPE.PARTNER, USER_TYPE.CUSTOMER],
		requireMoveTicketAccess: false,
	},
	[FASTSHIFT_PATH_KEYS.SELECTION]: {
		allowedUserTypes: [USER_TYPE.INTERNAL, USER_TYPE.PARTNER],
		requireMoveTicketAccess: false,
	},
	[FASTSHIFT_PATH_KEYS.RESOURCES_PARTNER_SCOPED]: {
		allowedUserTypes: [USER_TYPE.INTERNAL, USER_TYPE.PARTNER],
		requireMoveTicketAccess: false,
	},
	[FASTSHIFT_PATH_KEYS.OVERVIEW]: {
		allowedUserTypes: [USER_TYPE.INTERNAL, USER_TYPE.PARTNER, USER_TYPE.CUSTOMER],
		requireMoveTicketAccess: false,
	},
	[FASTSHIFT_PATH_KEYS.TICKETS]: {
		allowedUserTypes: [USER_TYPE.INTERNAL, USER_TYPE.PARTNER, USER_TYPE.CUSTOMER],
		requireMoveTicketAccess: true,
	},
	[FASTSHIFT_PATH_KEYS.PLANNING]: {
		allowedUserTypes: [USER_TYPE.INTERNAL, USER_TYPE.PARTNER, USER_TYPE.CUSTOMER],
		requireMoveTicketAccess: true,
	},
	[FASTSHIFT_PATH_KEYS.RESOURCES_DOMAIN_SCOPED]: {
		allowedUserTypes: [USER_TYPE.INTERNAL, USER_TYPE.PARTNER, USER_TYPE.CUSTOMER],
		requireMoveTicketAccess: false,
	},
};

export const canUserAccessRoutePolicy = (
	routePolicy: FastShiftRouteAccessPolicy,
	user: UserAccess['data'],
): boolean => {
	if (!user || !routePolicy.allowedUserTypes.includes(user.userType)) {
		return false;
	}

	if (routePolicy.requireMoveTicketAccess && !user.hasMoveTicketAccess) {
		return false;
	}

	return true;
};

export const canUserAccessRoutePolicyByUserType = (
	routePolicy: FastShiftRouteAccessPolicy,
	user: UserAccess['data'],
): boolean => {
	if (!user) {
		return false;
	}

	return routePolicy.allowedUserTypes.includes(user.userType);
};

export const routeRequiresMoveTicketAccess = (routeKey: FASTSHIFT_PATH_KEYS): boolean =>
	FASTSHIFT_ROUTE_ACCESS_POLICIES[routeKey].requireMoveTicketAccess;

export const getRouteKeyForPathname = (
	pathname: string,
	paths: Record<string, string | null>,
): FASTSHIFT_PATH_KEYS | undefined => {
	const routeKeyByPathname = new Map<string, FASTSHIFT_PATH_KEYS>();

	const pathEntries: Array<[string | null | undefined, FASTSHIFT_PATH_KEYS]> = [
		[paths['home'], FASTSHIFT_PATH_KEYS.FASTSHIFT],
		[paths['selection'], FASTSHIFT_PATH_KEYS.SELECTION],
		[paths['partnerResources'], FASTSHIFT_PATH_KEYS.RESOURCES_PARTNER_SCOPED],
		[paths['domainScopedOverview'], FASTSHIFT_PATH_KEYS.OVERVIEW],
		[paths['domainScopedTickets'], FASTSHIFT_PATH_KEYS.TICKETS],
		[paths['domainScopedPlanning'], FASTSHIFT_PATH_KEYS.PLANNING],
		[paths['domainScopedResources'], FASTSHIFT_PATH_KEYS.RESOURCES_DOMAIN_SCOPED],
	];

	pathEntries.forEach(([path, routeKey]) => {
		if (path) {
			routeKeyByPathname.set(path, routeKey);
		}
	});

	return routeKeyByPathname.get(pathname);
};
